import { RefreshCw, TimerOff, Trash2, Zap } from "lucide-react";
import { useEffect, useState } from "react";
import { MediaThumb } from "../components/MediaThumb";
import { ApiError, api } from "../lib/api";
import type { ScheduledItem } from "../lib/types";

function statusClass(status: ScheduledItem["status"]) {
  if (status === "pending") return "bg-amber-50 text-amber-700";
  if (status === "sending") return "bg-sky-50 text-sky-700";
  if (status === "sent") return "bg-emerald-50 text-emerald-700";
  if (status === "cancelled") return "bg-slate-100 text-slate-600";
  return "bg-red-50 text-red-700";
}

function targetLabel(item: ScheduledItem) {
  const type = item.target === "group" ? "Group" : "Channel";
  return `[${type}] ${item.targetName ?? item.targetTelegramId ?? item.channelId}`;
}

export function Scheduled() {
  const [items, setItems] = useState<ScheduledItem[]>([]);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const result = await api.scheduled();
      setItems(result.items);
      setError("");
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "โหลด scheduled posts ไม่สำเร็จ");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const cancel = async (item: ScheduledItem) => {
    if (!window.confirm("ยกเลิกโพสต์ที่ตั้งเวลาไว้นี้ใช่ไหม?")) return;
    try {
      await api.cancelScheduled(item.id);
      setNotice("ยกเลิก scheduled post แล้ว");
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "ยกเลิก scheduled post ไม่สำเร็จ");
    }
  };

  const remove = async (item: ScheduledItem) => {
    if (!window.confirm("ลบรายการ Scheduled นี้ออกจาก list ใช่ไหม?")) return;
    try {
      await api.deleteScheduled(item.id);
      setNotice("ลบ scheduled post แล้ว");
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "ลบ scheduled post ไม่สำเร็จ");
    }
  };

  const clearCompleted = async () => {
    if (!window.confirm("เคลียร์รายการ sent / failed / cancelled ทั้งหมดใช่ไหม? รายการ pending จะไม่ถูกลบ")) return;
    try {
      const result = await api.clearCompletedScheduled();
      setNotice(`เคลียร์รายการที่จบแล้ว ${result.cleared} รายการ`);
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "เคลียร์ completed scheduled posts ไม่สำเร็จ");
    }
  };

  const processDue = async () => {
    try {
      const result = await api.processScheduledDue();
      setNotice(`ประมวลผล due scheduled posts แล้ว (${result.processed} รายการ)`);
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "ประมวลผล scheduled posts ไม่สำเร็จ");
    }
  };

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-ink">Scheduled Posts</h1>
          <p className="mt-1 text-sm text-slate-500">รายการโพสต์ที่ Cron จะตรวจทุก 5 นาทีเมื่อถึงเวลา</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={clearCompleted}
            disabled={!items.some((item) => item.status === "sent" || item.status === "failed" || item.status === "cancelled")}
            className="inline-flex items-center gap-2 rounded border border-line bg-white px-3 py-2 text-sm font-semibold hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <Trash2 size={16} />
            Clear Completed
          </button>
          <button
            type="button"
            onClick={processDue}
            className="inline-flex items-center gap-2 rounded border border-line bg-white px-3 py-2 text-sm font-semibold hover:bg-slate-100"
          >
            <Zap size={16} />
            Process Due
          </button>
          <button
            type="button"
            onClick={load}
            disabled={loading}
            className="inline-flex items-center gap-2 rounded border border-line bg-white px-3 py-2 text-sm font-semibold hover:bg-slate-100 disabled:opacity-60"
          >
            <RefreshCw size={16} className={loading ? "animate-spin" : ""} />
            Refresh
          </button>
        </div>
      </div>

      {notice ? <div className="rounded border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-700">{notice}</div> : null}
      {error ? <div className="rounded border border-red-200 bg-red-50 p-3 text-sm text-red-700">{error}</div> : null}

      <div className="overflow-hidden rounded border border-line bg-white shadow-sm">
        <table className="w-full min-w-[1060px] text-left text-sm">
          <thead className="bg-slate-50 text-xs uppercase text-slate-500">
            <tr>
              <th className="px-4 py-3">Scheduled Time</th>
              <th className="px-4 py-3">Target</th>
              <th className="px-4 py-3">Media / Preview</th>
              <th className="px-4 py-3">Title / Caption</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3">Error</th>
              <th className="px-4 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.map((item) => (
              <tr key={item.id} className="border-t border-line">
                <td className="px-4 py-3">{new Date(item.scheduledAt).toLocaleString("th-TH")}</td>
                <td className="px-4 py-3">{targetLabel(item)}</td>
                <td className="px-4 py-3"><MediaThumb preview={item.mediaPreview} mediaType={item.mediaType} /></td>
                <td className="max-w-[280px] px-4 py-3">
                  <div className="truncate font-medium text-ink">{item.title || "-"}</div>
                  <div className="truncate text-xs text-slate-500">{item.caption}</div>
                </td>
                <td className="px-4 py-3">
                  <span className={`rounded px-2 py-1 text-xs font-semibold ${statusClass(item.status)}`}>{item.status}</span>
                </td>
                <td className="max-w-[240px] truncate px-4 py-3 text-red-600">{item.error ?? "-"}</td>
                <td className="px-4 py-3 text-right">
                  {item.status === "pending" ? (
                    <button
                      type="button"
                      onClick={() => cancel(item)}
                      className="inline-flex items-center gap-1 rounded border border-red-200 px-2.5 py-1.5 text-sm font-semibold text-red-600 hover:bg-red-50"
                    >
                      <Trash2 size={15} />
                      Cancel
                    </button>
                  ) : item.status === "sent" || item.status === "failed" || item.status === "cancelled" ? (
                    <button
                      type="button"
                      onClick={() => remove(item)}
                      className="inline-flex items-center gap-1 rounded border border-red-200 px-2.5 py-1.5 text-sm font-semibold text-red-600 hover:bg-red-50"
                    >
                      <Trash2 size={15} />
                      Delete
                    </button>
                  ) : (
                    <span className="text-xs text-slate-400">-</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {items.length === 0 ? (
          <div className="flex items-center gap-2 p-6 text-sm text-slate-500">
            <TimerOff size={17} />
            ยังไม่มี scheduled post
          </div>
        ) : null}
      </div>
    </div>
  );
}
